import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import config from "../../Config";
import StudentRightsCheck from "./StudentRightsCheck";
import StudentPanelMenu from "./StudentPanelMenu";

function StudentPanelClassTimeTable() {
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(parseInt(localStorage.getItem("ID"), 10) || 0);
  const [MemberType] = useState(localStorage.getItem("MemberType") || "Student");
  const [SessionID] = useState(parseInt(localStorage.getItem("DefaultSession"), 10) || 0);
  const [studentName] = useState(localStorage.getItem("FullName") || localStorage.getItem("UserName") || "Student");

  const [loading, setLoading] = useState(true);
  const [header, setHeader] = useState(null);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    document.title = "Class Time Table - Student Panel";
    axios({
      method: "post",
      url: config.base_url + "Student/GetMyClassTimeTable",
      headers: { "Content-Type": "application/json" },
      data: { AccessKey, UserID, MemberType, SessionID },
    })
      .then((res) => {
        if (res.data.status_code === 1) {
          setHeader(res.data.TimeTable || null);
          setRows(res.data.ListofRecords || []);
        } else {
          setHeader(null);
          setRows([]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const days = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      if (!map[r.Day_ID]) map[r.Day_ID] = r.Day_Name || `Day ${r.Day_ID}`;
    });
    return Object.keys(map)
      .map((k) => ({ id: parseInt(k, 10), name: map[k] }))
      .sort((a, b) => a.id - b.id);
  }, [rows]);

  const periods = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      if (!map[r.Period_ID]) map[r.Period_ID] = r.Period_Name || `Period ${r.Period_ID}`;
    });
    return Object.keys(map)
      .map((k) => ({ id: parseInt(k, 10), name: map[k] }))
      .sort((a, b) => a.id - b.id);
  }, [rows]);

  const cellText = (dayId, periodId) => {
    const row = rows.find((x) => x.Day_ID === dayId && x.Period_ID === periodId);
    if (!row) return "";
    if (row.Is_Break) return "Break";
    if (row.Is_FreePeriod) return "Free Period";
    const sub = row.Subject_Name || "";
    const tea = row.Teacher_Name || "";
    return tea ? `${sub} (${tea})` : sub;
  };

  const exportCsv = () => {
    if (rows.length === 0) return;
    const headers = ["Time", ...days.map((d) => d.name)];
    const lines = [headers.map((h) => `"${String(h).replace(/"/g, '""')}"`).join(",")];
    periods.forEach((p) => {
      const row = [p.name, ...days.map((d) => cellText(d.id, p.id))];
      lines.push(row.map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "student-class-timetable.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const shellStyle = {
    background: "linear-gradient(120deg, #0f4cad 0%, #8b4cd8 100%)",
    borderRadius: 14,
    padding: 18,
  };

  const timeHeadStyle = {
    background: "#58d5df",
    color: "#0b1330",
    fontWeight: 700,
    textAlign: "center",
    border: "6px solid transparent",
    borderRadius: 8,
    minWidth: 140,
  };

  const dayHeadStyle = {
    background: "#f7ced5",
    color: "#1f2440",
    fontWeight: 700,
    textAlign: "center",
    border: "6px solid transparent",
    borderRadius: 8,
    minWidth: 170,
  };

  const timeCellStyle = {
    background: "#73d8df",
    color: "#10183d",
    fontWeight: 600,
    textAlign: "center",
    border: "6px solid transparent",
    borderRadius: 8,
  };

  const slotCellStyle = {
    background: "#f3f4f6",
    color: "#1c2648",
    border: "6px solid transparent",
    borderRadius: 8,
    minHeight: 62,
    verticalAlign: "middle",
    fontSize: 14,
  };

  return (
    <>
      <StudentPanelMenu />
      <StudentRightsCheck />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item"><Link to="/student-panel/dashboard">Dashboard</Link></li>
                    <li className="breadcrumb-item active">Class Time Table</li>
                  </ol>
                </nav>
                <div className="page-title">Class Time Table</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container body-content">
        <div className="card">
          <div className="card-body">
            {loading ? (
              <Spinner animation="border" />
            ) : !header ? (
              <p className="text-muted mb-0">No active timetable available right now.</p>
            ) : (
              <>
                <div style={shellStyle}>
                  <div className="row align-items-center mb-3">
                    <div className="col-md-7">
                      <h2 style={{ color: "#ffffff", fontWeight: 800, marginBottom: 0 }}>Class Schedule</h2>
                    </div>
                    <div className="col-md-5">
                      <div style={{ background: "#f5f5f5", borderRadius: 8, padding: "10px 14px" }}>
                        <div style={{ fontWeight: 700, color: "#111827" }}>Name : {studentName}</div>
                        <div style={{ fontWeight: 700, color: "#111827" }}>
                          Subject : {header && header.Name ? header.Name : "Class Time Table"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {header ? (
                    <p className="small mb-3" style={{ color: "#e8ecff" }}>
                      Effective: {header.EffectiveFrom} {header.EffectiveTo ? `to ${header.EffectiveTo}` : ""}
                    </p>
                  ) : null}
                  <div className="mb-3 d-flex gap-2">
                    <button type="button" className="btn btn-light btn-sm" onClick={() => window.print()}>Print</button>
                    <button type="button" className="btn btn-light btn-sm" onClick={exportCsv}>Export CSV</button>
                  </div>

                  {rows.length === 0 ? (
                  <p className="text-muted mb-0">Timetable header exists but no period rows are saved yet.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-borderless table-sm mb-0">
                      <thead>
                        <tr>
                          <th style={timeHeadStyle}>TIME</th>
                          {days.map((d) => <th key={d.id} style={dayHeadStyle}>{String(d.name || "").toUpperCase()}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {periods.map((p) => (
                          <tr key={p.id}>
                            <td style={timeCellStyle}>{p.name}</td>
                            {days.map((d) => (
                              <td key={`${d.id}_${p.id}`} style={slotCellStyle}>{cellText(d.id, p.id)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default StudentPanelClassTimeTable;
